import React, {useEffect} from 'react';
import useTirePressure from '@di-shared/features/diag/tire/useTirePressure';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import clsx from 'clsx';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import {emitEventLog} from '@di-shared/features/analytics';
import {TirePressureStatus} from '@di-shared/features/diag/tire/const';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

interface TirePressureViewerProps {
  vin: string;
}

enum InvalidOrAbnormal {
  Invalid = 'Invalid',
  Abnormal = 'Abnormal',
}

function TirePressureViewer({vin}: TirePressureViewerProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {myTire, updatedTime, pressureUnit} = useTirePressure(vin);
  useEffect(() => {
    (async function () {
      const response = await ccWebviewUtils.getCarWarningLightStatus();
      if (
        response?.tire?.frontRight === InvalidOrAbnormal.Abnormal ||
        response?.tire?.frontLeft === InvalidOrAbnormal.Abnormal ||
        response?.tire?.rearRight === InvalidOrAbnormal.Abnormal ||
        response?.tire?.rearLeft === InvalidOrAbnormal.Abnormal
      ) {
        await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_DIAG_TIRE_WARNING.getFullPath());
        emitEventLog({
          event: 'vehicle_status_check',
          interaction_type: 'view tire pressure faq',
          click_text: t('T10114'),
          click_location: 'body',
        }); // 바텀시트가 닫히는 타이밍이 너무 빨라서 분석 api 를 못쏠까봐 바텀시트 닫히고 저장
      }
    })();
  }, [myTire, t]);
  return (
    <>
      <div className="section_visual visual_tire_manage">
        <ImageSelector
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/car_management/tire_pressure_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/car_management/tire_pressure.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_pressure.png')}
        />
        <div className="tire_info_box">
          <div className={clsx('tire_info front left', {type_caution: myTire.fl.status !== TirePressureStatus.INBOUND})}>
            <div className="tire_value">
              <span className="tire_pressure_number">{myTire.fl.pressure}</span>
              <span className="tire_pressure_unit">{pressureUnit}</span>
            </div>
          </div>
          <div className={clsx('tire_info front right', {type_caution: myTire.fr.status !== TirePressureStatus.INBOUND})}>
            <div className="tire_value">
              <span className="tire_pressure_number">{myTire.fr.pressure}</span>
              <span className="tire_pressure_unit">{pressureUnit}</span>
            </div>
          </div>
          <div className={clsx('tire_info rear left', {type_caution: myTire.rl.status !== TirePressureStatus.INBOUND})}>
            <div className="tire_value">
              <span className="tire_pressure_number">{myTire.rl.pressure}</span>
              <span className="tire_pressure_unit">{pressureUnit}</span>
            </div>
          </div>
          <div className={clsx('tire_info rear right', {type_caution: myTire.rr.status !== TirePressureStatus.INBOUND})}>
            <div className="tire_value">
              <span className="tire_pressure_number">{myTire.rr.pressure}</span>
              <span className="tire_pressure_unit">{pressureUnit}</span>
            </div>
          </div>
        </div>
        <span className="tire_info_date">
          {t('T30001')}: {updatedTime && CldrDatetimeFormatter.format(updatedTime, 'yMMMaHM')}
        </span>
      </div>
    </>
  );
}

export default TirePressureViewer;

export function TirePressureViewerError() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_visual visual_tire_manage">
        <ImageSelector
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/car_management/tire_pressure_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/car_management/tire_pressure.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_pressure_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_pressure.png')}
        />
        <div className="tire_info_box">
          <div className={`tire_info front left`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
          <div className={`tire_info front right`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
          <div className={`tire_info rear left`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
          <div className={`tire_info rear right`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
        </div>
        <span className="tire_info_date">{t('T30001')}:</span>
      </div>
    </>
  );
}

export function TirePressureViewerLoading() {
  return (
    <>
      <div className="section_visual visual_tire_manage skeleton">
        <ImageSelector
          className="section_visual_img"
          alt="section_visual_img"
          kiaLightSrc={() => import('@di-shared/assets/img/car_management/tire_pressure_light.png')}
          kiaDarkSrc={() => import('@di-shared/assets/img/car_management/tire_pressure.png')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure_light.png')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/car_management/hyundai/tire_pressure.png')}
          genesisLightSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_pressure_light.png')}
          genesisDarkSrc={() => import('@di-shared/assets/img/car_management/genesis/tire_pressure.png')}
        />
        <div className="tire_info_box">
          <div className={`tire_info front left`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
          <div className={`tire_info front right`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
          <div className={`tire_info rear left`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
          <div className={`tire_info rear right`}>
            <div className="tire_value">
              <span className="tire_pressure_number">-</span>
              <span className="tire_pressure_unit">PSI</span>
            </div>
          </div>
        </div>
        <span className="tire_info_date"></span>
      </div>
    </>
  );
}
